import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import {
  User,
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Target,
  Calendar,
  Mail,
  CheckCircle,
  Loader2
} from 'lucide-react';
import Navbar from './shared/Navbar.jsx';
import { sharedStyles, createGlassCard, createButton } from './shared/sharedStyles.js';
import { useApi } from '../utils/api.js';

/**
 * GuestIntakeForm - Onboarding form for potential guests looking for shows
 *
 * GUEST-SIDE mirror of IntakeForm.jsx. Collects guest profile information
 * and generates show recommendations based on their expertise.
 */
const GuestIntakeForm = () => {
  const navigate = useNavigate();
  const { user, isSignedIn, isLoaded } = useUser();
  const { makeRequest } = useApi();

  const formStyles = {
    label: {
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '0.8rem',
      fontWeight: '500',
      color: sharedStyles.colors.textSecondary
    },
    input: {
      ...sharedStyles.inputStyles,
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '0.875rem',
      padding: '0.5rem 0.75rem'
    },
    textarea: {
      ...sharedStyles.inputStyles,
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '0.875rem',
      padding: '0.5rem 0.75rem',
      resize: 'vertical'
    },
    error: {
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSize: '0.75rem',
      color: sharedStyles.colors.error,
      marginTop: '0.25rem'
    }
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Professional Identity
    headline: '',
    bio: '',
    company: '',
    title: '',

    // Expertise & Topics
    expertise_areas: [],
    topics: [],
    talking_points: '',

    // Show Preferences
    ideal_show_profile: '',
    preferred_formats: [],
    preferred_audience_size: '',
    preferred_categories: [],

    // Availability & Logistics
    availability: '',
    timezone: '',
    recording_preferences: [],

    // Contact Information
    email: '',
    linkedin: '',
    x_account: '',
    website: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalSteps = 5;

  // Pre-fill email from Clerk user
  useEffect(() => {
    if (isLoaded && isSignedIn && user?.primaryEmailAddress?.emailAddress) {
      setFormData(prev => ({
        ...prev,
        email: user.primaryEmailAddress.emailAddress
      }));
    }
  }, [isLoaded, isSignedIn, user]);

  // Check for pending onboarding after sign-in
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      const pendingData = localStorage.getItem('pendingGuestOnboarding');
      if (pendingData) {
        try {
          const savedFormData = JSON.parse(pendingData);
          setFormData(savedFormData);
        } catch (error) {
          console.error('Error loading pending onboarding data:', error);
          localStorage.removeItem('pendingGuestOnboarding');
        }
      }
    }
  }, [isLoaded, isSignedIn]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleArrayToggle = (field, value, maxItems = 5) => {
    const currentArray = formData[field] || [];
    if (currentArray.includes(value)) {
      handleInputChange(field, currentArray.filter(item => item !== value));
    } else if (currentArray.length < maxItems) {
      handleInputChange(field, [...currentArray, value]);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.headline.trim()) newErrors.headline = 'Professional headline is required';
        if (!formData.bio.trim()) {
          newErrors.bio = 'Bio is required';
        } else if (formData.bio.trim().length < 200) {
          newErrors.bio = `Bio must be at least 200 characters (${formData.bio.trim().length}/200)`;
        }
        break;
      case 2:
        if (!formData.expertise_areas || formData.expertise_areas.length === 0) {
          newErrors.expertise_areas = 'Please select at least one expertise area';
        }
        if (!formData.topics || formData.topics.length === 0) {
          newErrors.topics = 'Please select at least one topic';
        }
        break;
      case 3:
        if (!formData.ideal_show_profile.trim()) {
          newErrors.ideal_show_profile = 'Please describe your ideal show';
        } else if (formData.ideal_show_profile.trim().length < 100) {
          newErrors.ideal_show_profile = `Description must be at least 100 characters (${formData.ideal_show_profile.trim().length}/100)`;
        }
        break;
      case 4:
        if (!formData.availability) newErrors.availability = 'Please select your availability';
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    if (!isLoaded || !isSignedIn) {
      localStorage.setItem('pendingGuestOnboarding', JSON.stringify(formData));
      navigate('/sign-in?redirect_url=' + encodeURIComponent('/guest/intake-form'));
      return;
    }

    setIsSubmitting(true);

    try {
      const data = await makeRequest('guest/onboarding', {
        method: 'POST',
        body: JSON.stringify(formData),
      });

      localStorage.removeItem('pendingGuestOnboarding');
      navigate(`/guest/show-matches/${data.show_match_id}`);
    } catch (error) {
      console.error('Guest onboarding error:', error);
      alert(`Failed to create profile: ${error.message}`);
      setIsSubmitting(false);
    }
  };

  const renderProgressBar = () => (
    <div style={{
      ...createGlassCard('subtle'),
      padding: '0.75rem',
      marginBottom: '1rem'
    }}>
      <div style={sharedStyles.strokeStyles} />

      <div style={{
        width: '100%',
        height: '4px',
        background: sharedStyles.colors.secondaryLight,
        borderRadius: '2px',
        overflow: 'hidden',
        marginBottom: '0.75rem'
      }}>
        <div
          style={{
            width: `${(currentStep / totalSteps) * 100}%`,
            height: '100%',
            background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
            borderRadius: '2px',
            transition: 'width 0.3s ease',
            boxShadow: '0px 1px 2px rgba(234, 88, 12, 0.2)'
          }}
        />
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        {Array.from({ length: totalSteps }, (_, i) => (
          <div
            key={i + 1}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: currentStep >= i + 1
                ? `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`
                : sharedStyles.colors.secondaryLight,
              color: currentStep >= i + 1 ? 'white' : sharedStyles.colors.textMuted,
              fontSize: '0.65rem',
              fontWeight: '600',
              transition: 'all 0.3s ease'
            }}
          >
            {currentStep > i + 1 ? (
              <CheckCircle size={12} />
            ) : (
              <span>{i + 1}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderChipSelector = (options, field, maxItems = 5, error = null) => (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.4rem',
      padding: '0.6rem',
      border: `2px solid ${error ? sharedStyles.colors.error : sharedStyles.colors.secondaryLight}`,
      borderRadius: '8px',
      background: '#FFFFFF'
    }}>
      {options.map(option => {
        const isSelected = (formData[field] || []).includes(option.value);
        const isDisabled = (formData[field] || []).length >= maxItems && !isSelected;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => handleArrayToggle(field, option.value, maxItems)}
            disabled={isDisabled}
            style={{
              padding: '0.3rem 0.6rem',
              background: isSelected
                ? `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`
                : 'white',
              border: `1px solid ${isSelected ? sharedStyles.colors.primary : sharedStyles.colors.secondaryLight}`,
              borderRadius: '16px',
              fontSize: '0.75rem',
              fontFamily: 'Inter, system-ui, sans-serif',
              color: isSelected ? 'white' : isDisabled ? sharedStyles.colors.textMuted : sharedStyles.colors.textSecondary,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: isDisabled ? 0.5 : 1,
              fontWeight: isSelected ? '500' : 'normal'
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );

  // Step 1: Professional Identity
  const renderStep1 = () => (
    <div style={{ ...createGlassCard(), padding: '1rem' }}>
      <div style={sharedStyles.strokeStyles} />

      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '36px',
          height: '36px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
          color: 'white',
          marginBottom: '0.5rem',
          boxShadow: '0px 4px 12px rgba(234, 88, 12, 0.25)'
        }}>
          <User size={18} />
        </div>
        <h2 style={{ ...sharedStyles.heading, fontSize: '1.25rem', marginBottom: '0.25rem' }}>
          Tell us about yourself
        </h2>
        <p style={{ ...sharedStyles.bodyText, color: sharedStyles.colors.textMuted, fontSize: '0.8rem' }}>
          Help podcast hosts understand who you are
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <label htmlFor="headline" style={formStyles.label}>Professional Headline *</label>
          <input
            id="headline"
            type="text"
            style={{
              ...formStyles.input,
              borderColor: errors.headline ? sharedStyles.colors.error : sharedStyles.colors.secondaryLight
            }}
            placeholder="e.g., AI Researcher & Startup Founder"
            value={formData.headline}
            onChange={(e) => handleInputChange('headline', e.target.value)}
          />
          {errors.headline && <span style={formStyles.error}>{errors.headline}</span>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <label htmlFor="title" style={formStyles.label}>Current Role/Title</label>
          <input
            id="title"
            type="text"
            style={formStyles.input}
            placeholder="e.g., CEO & Co-Founder"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <label htmlFor="company" style={formStyles.label}>Company/Organization</label>
          <input
            id="company"
            type="text"
            style={formStyles.input}
            placeholder="e.g., TechVentures Inc."
            value={formData.company}
            onChange={(e) => handleInputChange('company', e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', gridColumn: '1 / -1' }}>
          <label htmlFor="bio" style={formStyles.label}>
            Your Bio *
            <span style={{ fontWeight: '400', fontSize: '0.75rem', color: sharedStyles.colors.textMuted, marginLeft: '0.5rem' }}>
              (The longer the better)
            </span>
            <span style={{
              fontSize: '0.75rem',
              color: formData.bio.length < 200 ? sharedStyles.colors.error : sharedStyles.colors.textMuted,
              marginLeft: '0.5rem'
            }}>
              {formData.bio.length}/200 min
            </span>
          </label>
          <textarea
            id="bio"
            style={{
              ...formStyles.textarea,
              minHeight: '100px',
              borderColor: errors.bio ? sharedStyles.colors.error : sharedStyles.colors.secondaryLight
            }}
            placeholder="Tell podcast hosts about your background, experience, and what makes you a compelling guest..."
            rows="4"
            value={formData.bio}
            onChange={(e) => handleInputChange('bio', e.target.value)}
          />
          {errors.bio && <span style={formStyles.error}>{errors.bio}</span>}
        </div>
      </div>
    </div>
  );

  // Step 2: Expertise & Topics
  const renderStep2 = () => (
    <div style={{ ...createGlassCard(), padding: '1.5rem' }}>
      <div style={sharedStyles.strokeStyles} />

      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
          color: 'white',
          marginBottom: '1rem',
          boxShadow: '0px 4px 12px rgba(234, 88, 12, 0.25)'
        }}>
          <Briefcase size={20} />
        </div>
        <h2 style={{ ...sharedStyles.heading, fontSize: '1.5rem', marginBottom: '0.5rem' }}>
          Your expertise
        </h2>
        <p style={{ ...sharedStyles.bodyText, color: sharedStyles.colors.textMuted }}>
          What topics can you discuss on podcasts?
        </p>
      </div>

      <div style={{ display: 'grid', gap: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label style={formStyles.label}>
            Expertise Areas *
            <span style={{ fontSize: '0.75rem', color: sharedStyles.colors.textMuted, marginLeft: '0.5rem' }}>
              (Select up to 5)
            </span>
          </label>
          {renderChipSelector([
            { value: 'AI', label: 'AI & Machine Learning' },
            { value: 'Startups', label: 'Startups' },
            { value: 'Marketing', label: 'Marketing' },
            { value: 'Leadership', label: 'Leadership' },
            { value: 'Product', label: 'Product Management' },
            { value: 'Finance', label: 'Finance' },
            { value: 'Sales', label: 'Sales' },
            { value: 'Engineering', label: 'Engineering' },
            { value: 'Design', label: 'Design' },
            { value: 'Health', label: 'Health & Wellness' },
            { value: 'Science', label: 'Science' },
            { value: 'Education', label: 'Education' },
            { value: 'Other', label: 'Other' }
          ], 'expertise_areas', 5, errors.expertise_areas)}
          {errors.expertise_areas && <span style={formStyles.error}>{errors.expertise_areas}</span>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label style={formStyles.label}>
            Topics You Can Discuss *
            <span style={{ fontSize: '0.75rem', color: sharedStyles.colors.textMuted, marginLeft: '0.5rem' }}>
              (Select up to 5)
            </span>
          </label>
          {renderChipSelector([
            { value: 'founder_journey', label: 'Founder Journey' },
            { value: 'fundraising', label: 'Fundraising' },
            { value: 'growth', label: 'Growth Strategies' },
            { value: 'career', label: 'Career Advice' },
            { value: 'innovation', label: 'Innovation' },
            { value: 'future_of_work', label: 'Future of Work' },
            { value: 'diversity', label: 'Diversity & Inclusion' },
            { value: 'mental_health', label: 'Mental Health' },
            { value: 'productivity', label: 'Productivity' },
            { value: 'industry_trends', label: 'Industry Trends' }
          ], 'topics', 5, errors.topics)}
          {errors.topics && <span style={formStyles.error}>{errors.topics}</span>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="talking_points" style={formStyles.label}>Key Talking Points</label>
          <textarea
            id="talking_points"
            style={{ ...formStyles.textarea, minHeight: '80px' }}
            placeholder="What unique stories, insights, or perspectives can you share? e.g., 'My journey from PhD researcher to startup founder, lessons from failing my first startup'"
            rows="3"
            value={formData.talking_points}
            onChange={(e) => handleInputChange('talking_points', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  // Step 3: Show Preferences
  const renderStep3 = () => (
    <div style={{ ...createGlassCard(), padding: '1.5rem' }}>
      <div style={sharedStyles.strokeStyles} />

      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
          color: 'white',
          marginBottom: '1rem',
          boxShadow: '0px 4px 12px rgba(234, 88, 12, 0.25)'
        }}>
          <Target size={20} />
        </div>
        <h2 style={{ ...sharedStyles.heading, fontSize: '1.5rem', marginBottom: '0.5rem' }}>
          Ideal podcast
        </h2>
        <p style={{ ...sharedStyles.bodyText, color: sharedStyles.colors.textMuted }}>
          What kind of shows would you like to appear on?
        </p>
      </div>

      <div style={{ display: 'grid', gap: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="ideal_show_profile" style={formStyles.label}>
            Describe Your Ideal Show *
            <span style={{
              fontSize: '0.75rem',
              color: formData.ideal_show_profile.length < 100 ? sharedStyles.colors.error : sharedStyles.colors.textMuted,
              marginLeft: '0.5rem'
            }}>
              {formData.ideal_show_profile.length}/100 min
            </span>
          </label>
          <textarea
            id="ideal_show_profile"
            style={{
              ...formStyles.textarea,
              minHeight: '100px',
              borderColor: errors.ideal_show_profile ? sharedStyles.colors.error : sharedStyles.colors.secondaryLight
            }}
            placeholder="Describe the type of podcasts you'd love to appear on. What topics should they cover? What kind of audience? What style of conversation?"
            rows="4"
            value={formData.ideal_show_profile}
            onChange={(e) => handleInputChange('ideal_show_profile', e.target.value)}
          />
          {errors.ideal_show_profile && <span style={formStyles.error}>{errors.ideal_show_profile}</span>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label style={formStyles.label}>Preferred Categories</label>
          {renderChipSelector([
            { value: 'Business', label: 'Business' },
            { value: 'Technology', label: 'Technology' },
            { value: 'Entrepreneurship', label: 'Entrepreneurship' },
            { value: 'Marketing', label: 'Marketing' },
            { value: 'Science', label: 'Science' },
            { value: 'Education', label: 'Education' },
            { value: 'Health', label: 'Health & Wellness' },
            { value: 'Lifestyle', label: 'Lifestyle' }
          ], 'preferred_categories', 4)}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label style={formStyles.label}>Preferred Formats</label>
          {renderChipSelector([
            { value: 'Interview', label: 'Interview' },
            { value: 'Panel', label: 'Panel Discussion' },
            { value: 'Conversational', label: 'Conversational' },
            { value: 'Educational', label: 'Educational' },
            { value: 'Storytelling', label: 'Storytelling' }
          ], 'preferred_formats', 3)}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="preferred_audience_size" style={formStyles.label}>Preferred Audience Size</label>
          <select
            id="preferred_audience_size"
            style={formStyles.input}
            value={formData.preferred_audience_size}
            onChange={(e) => handleInputChange('preferred_audience_size', e.target.value)}
          >
            <option value="">No preference</option>
            <option value="any">Any size</option>
            <option value="micro">Micro (under 1,000)</option>
            <option value="small">Small (1,000-10,000)</option>
            <option value="medium">Medium (10,000-50,000)</option>
            <option value="large">Large (50,000+)</option>
          </select>
        </div>
      </div>
    </div>
  );

  // Step 4: Availability & Logistics
  const renderStep4 = () => (
    <div style={{ ...createGlassCard(), padding: '1.5rem' }}>
      <div style={sharedStyles.strokeStyles} />

      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
          color: 'white',
          marginBottom: '1rem',
          boxShadow: '0px 4px 12px rgba(234, 88, 12, 0.25)'
        }}>
          <Calendar size={20} />
        </div>
        <h2 style={{ ...sharedStyles.heading, fontSize: '1.5rem', marginBottom: '0.5rem' }}>
          Availability
        </h2>
        <p style={{ ...sharedStyles.bodyText, color: sharedStyles.colors.textMuted }}>
          When and how are you available to record?
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="availability" style={formStyles.label}>Availability *</label>
          <select
            id="availability"
            style={{
              ...formStyles.input,
              borderColor: errors.availability ? sharedStyles.colors.error : sharedStyles.colors.secondaryLight
            }}
            value={formData.availability}
            onChange={(e) => handleInputChange('availability', e.target.value)}
          >
            <option value="">Select availability</option>
            <option value="immediately">Immediately</option>
            <option value="1-2-weeks">1-2 weeks</option>
            <option value="2-4-weeks">2-4 weeks</option>
            <option value="1-2-months">1-2 months</option>
            <option value="flexible">Flexible</option>
          </select>
          {errors.availability && <span style={formStyles.error}>{errors.availability}</span>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="timezone" style={formStyles.label}>Your Timezone</label>
          <select
            id="timezone"
            style={formStyles.input}
            value={formData.timezone}
            onChange={(e) => handleInputChange('timezone', e.target.value)}
          >
            <option value="">Select timezone</option>
            <option value="America/New_York">Eastern Time (ET)</option>
            <option value="America/Chicago">Central Time (CT)</option>
            <option value="America/Denver">Mountain Time (MT)</option>
            <option value="America/Los_Angeles">Pacific Time (PT)</option>
            <option value="Europe/London">UK Time (GMT/BST)</option>
            <option value="Europe/Paris">Central European Time (CET)</option>
            <option value="Asia/Tokyo">Japan Time (JST)</option>
            <option value="Australia/Sydney">Sydney Time (AEST)</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', gridColumn: '1 / -1' }}>
          <label style={formStyles.label}>Recording Preferences</label>
          {renderChipSelector([
            { value: 'Remote', label: 'Remote (Zoom, etc.)' },
            { value: 'In-person', label: 'In-person' },
            { value: 'Phone', label: 'Phone' },
            { value: 'Flexible', label: 'Flexible' }
          ], 'recording_preferences', 3)}
        </div>
      </div>
    </div>
  );

  // Step 5: Contact Information
  const renderStep5 = () => (
    <div style={{ ...createGlassCard(), padding: '1.5rem' }}>
      <div style={sharedStyles.strokeStyles} />

      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${sharedStyles.colors.primary} 0%, ${sharedStyles.colors.primaryDark} 100%)`,
          color: 'white',
          marginBottom: '1rem',
          boxShadow: '0px 4px 12px rgba(234, 88, 12, 0.25)'
        }}>
          <Mail size={20} />
        </div>
        <h2 style={{ ...sharedStyles.heading, fontSize: '1.5rem', marginBottom: '0.5rem' }}>
          Contact information
        </h2>
        <p style={{ ...sharedStyles.bodyText, color: sharedStyles.colors.textMuted }}>
          How can podcast hosts reach you?
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="email" style={formStyles.label}>Contact Email</label>
          <input
            id="email"
            type="email"
            style={formStyles.input}
            placeholder="your@email.com"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="linkedin" style={formStyles.label}>LinkedIn Profile</label>
          <input
            id="linkedin"
            type="url"
            style={formStyles.input}
            placeholder="https://linkedin.com/in/yourprofile"
            value={formData.linkedin}
            onChange={(e) => handleInputChange('linkedin', e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="x_account" style={formStyles.label}>X/Twitter Handle</label>
          <input
            id="x_account"
            type="text"
            style={formStyles.input}
            placeholder="@yourhandle"
            value={formData.x_account}
            onChange={(e) => handleInputChange('x_account', e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <label htmlFor="website" style={formStyles.label}>Website</label>
          <input
            id="website"
            type="url"
            style={formStyles.input}
            placeholder="https://yourwebsite.com"
            value={formData.website}
            onChange={(e) => handleInputChange('website', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ ...sharedStyles.pageContainer, height: '100vh', overflow: 'hidden' }}>
      <Navbar variant="form" subtitle="Find Shows to Appear On" />

      <main style={{
        flex: 1,
        padding: '1rem',
        display: 'flex',
        justifyContent: 'center',
        height: 'calc(100vh - 80px)',
        overflowY: 'auto'
      }}>
        <div style={{ width: '100%', maxWidth: '800px' }}>
          {renderProgressBar()}

          <div style={{ marginBottom: '1rem' }}>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
            {currentStep === 5 && renderStep5()}
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '1rem'
          }}>
            {currentStep > 1 ? (
              <button
                type="button"
                style={{
                  ...createButton('secondary'),
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onClick={prevStep}
              >
                <ArrowLeft size={16} />
                Previous
              </button>
            ) : (
              <div />
            )}

            {currentStep < totalSteps ? (
              <button
                type="button"
                style={{
                  ...createButton('primary'),
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onClick={nextStep}
              >
                Next
                <ArrowRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                style={{
                  ...createButton('primary'),
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  opacity: isSubmitting ? 0.7 : 1
                }}
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    AI is finding shows (30-60s)...
                  </>
                ) : (
                  <>
                    Find My Shows
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </main>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default GuestIntakeForm;
